import { decodeServer, encode, type ClientMessage, type ServerMessage } from '@nanocars/shared';

const TICKS_PER_SECOND = 60;
const TICK_MS = 1000 / TICKS_PER_SECOND;
const PING_INTERVAL_MS = 2000;
const EWMA_ALPHA = 0.1;
const SNAP_THRESHOLD_TICKS = 10;
const SESSION_STORAGE_KEY = 'nanocars.session';

// The WebSocket OPEN readyState, hardcoded rather than read off the
// `WebSocket` global so a test double that doesn't define static
// readyState constants still behaves correctly.
const WS_OPEN = 1;

// Thin wrapper around a WebSocket that speaks the @nanocars/shared wire
// protocol: encodes/sends ClientMessage, decodes incoming ServerMessage and
// re-dispatches them as same-named events, and maintains a 2s ping/pong
// loop for rttMs and an EWMA-smoothed estimate of serverTickOffset.
export class NetClient extends EventTarget {
  private ws: WebSocket | null = null;
  // Aborted to detach every listener this class attached to `ws`. Held
  // separately from the socket so a socket being replaced can be silenced
  // BEFORE it is closed - otherwise its own 'close' event would run the
  // teardown belonging to the socket that just replaced it.
  private wsListeners: AbortController | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSeq = 0;
  private readonly pendingPings = new Map<number, number>();

  private _rttMs = 0;
  private _serverTickOffset = 0;
  private _session: string | null;

  constructor() {
    super();
    this._session = readSession();
  }

  get rttMs(): number {
    return this._rttMs;
  }

  get serverTickOffset(): number {
    return this._serverTickOffset;
  }

  get session(): string | null {
    return this._session;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WS_OPEN;
  }

  set session(value: string | null) {
    this._session = value;
    writeSession(value);
  }

  connect(url: string): Promise<void> {
    // A second connect() while one socket is already live (a reconnect
    // attempt racing a still-open socket) used to leave the old socket
    // attached: its listeners stayed bound, so its eventual 'close' ran
    // teardown against the NEW connection, and its messages kept being
    // dispatched as if they were the new socket's. Detach and close it
    // first, listeners off before close so its own close event is inert.
    this.closeSocket();

    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(url);
      const listeners = new AbortController();
      this.ws = ws;
      this.wsListeners = listeners;
      const { signal } = listeners;

      const onOpen = (): void => {
        if (settled) return;
        settled = true;
        this.startPing();
        resolve();
      };
      const onError = (): void => {
        if (settled) return;
        settled = true;
        reject(new Error('NetClient: WebSocket connection failed'));
      };

      ws.addEventListener('open', onOpen, { once: true, signal });
      ws.addEventListener('error', onError, { once: true, signal });
      ws.addEventListener('message', (ev: Event) => this.handleMessage(ev as MessageEvent), { signal });
      ws.addEventListener('close', () => this.stopPing(), { signal });
    });
  }

  // Detaches this class's listeners from the current socket, then closes it.
  // Order matters: listeners first, so the close below cannot re-enter
  // stopPing (or anything else) on behalf of a socket that is on its way out.
  private closeSocket(): void {
    const ws = this.ws;
    this.wsListeners?.abort();
    this.wsListeners = null;
    this.ws = null;
    ws?.close();
  }

  send(m: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WS_OPEN) return;
    this.ws.send(encode(m));
  }

  on<T extends ServerMessage['t']>(t: T, fn: (m: Extract<ServerMessage, { t: T }>) => void): () => void {
    const listener = (ev: Event): void => {
      fn((ev as CustomEvent<Extract<ServerMessage, { t: T }>>).detail);
    };
    this.addEventListener(t, listener);
    return () => this.removeEventListener(t, listener);
  }

  disconnect(): void {
    this.stopPing();
    this.closeSocket();
  }

  private handleMessage(ev: MessageEvent): void {
    if (typeof ev.data !== 'string') return;
    const msg = decodeServer(ev.data);
    if (!msg) return;
    if (msg.t === 'pong') this.handlePong(msg);
    this.dispatchEvent(new CustomEvent(msg.t, { detail: msg }));
  }

  private handlePong(msg: Extract<ServerMessage, { t: 'pong' }>): void {
    const sentAt = this.pendingPings.get(msg.c);
    if (sentAt === undefined) return;
    this.pendingPings.delete(msg.c);

    const rtt = performance.now() - sentAt;
    this._rttMs = rtt;

    const localTicksNow = performance.now() / TICK_MS;
    const sample = msg.serverTick + rtt / 2 / TICK_MS - localTicksNow;

    this._serverTickOffset =
      Math.abs(sample - this._serverTickOffset) > SNAP_THRESHOLD_TICKS
        ? sample
        : this._serverTickOffset + EWMA_ALPHA * (sample - this._serverTickOffset);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.ping(), PING_INTERVAL_MS);
    this.ping();
  }

  private ping(): void {
    const c = this.pingSeq++;
    this.pendingPings.set(c, performance.now());
    this.send({ t: 'ping', c });
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

function readSession(): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSession(value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(SESSION_STORAGE_KEY);
    else localStorage.setItem(SESSION_STORAGE_KEY, value);
  } catch {
    // Storage can throw (private browsing, quota); losing persistence isn't
    // fatal, so swallow it.
  }
}
